import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as create$3, F as FormController } from './form.js';
import { z as bankStatementValidation, A as voidedCheckValidation, B as routingNumberValidation, C as accountNumberValidation, D as accountTypeValidation, n as identityNameValidation, E as nicknameValidation, F as bankNameValidation } from './schema-validations.js';
import './Business.js';
import { B as BusinessFormStep } from './event-types.js';
import { h as heading2 } from './parts.js';
import { P as PaymentProvisioningLoading } from './payment-provisioning-loading.js';
import './utils2.js';
import { C as ComponentErrorSeverity, a as ComponentErrorCodes } from './ComponentError.js';
import './Checkout.js';
import './Pagination.js';
import './Payment.js';
import './Refund.js';
import { a as EntityDocumentStorage, b as EntityDocument } from './Document.js';
import { d as defineCustomElement$8 } from './bank-account-document-inputs.js';
import { d as defineCustomElement$7 } from './bank-account-form-inputs2.js';
import { d as defineCustomElement$6 } from './business-documents-on-file2.js';
import { d as defineCustomElement$5 } from './custom-popper2.js';
import { d as defineCustomElement$4 } from './form-control-file2.js';
import { d as defineCustomElement$3 } from './form-control-select2.js';
import { d as defineCustomElement$2 } from './form-control-text2.js';
import { d as defineCustomElement$1 } from './form-control-tooltip2.js';

class BankAccount {
    constructor(data) {
        this.id = data.id;
        this.account_owner_name = data.account_owner_name;
        this.full_name = data.full_name;
        this.account_type = data.account_type;
        this.account_number = data.account_number;
        this.acct_last_four = data.acct_last_four;
        this.account_number_last4 = data.account_number_last4;
        this.routing_number = data.routing_number;
        this.bank_name = data.bank_name;
        this.country = data.country;
        this.currency = data.currency;
        this.nickname = data.nickname;
        this.metadata = data.metadata;
        this.business_id = data.business_id;
        this.platform_account_id = data.platform_account_id;
        this.created_at = data.created_at;
        this.updated_at = data.updated_at;
    }
    get payload() {
        return {
            account_owner_name: this.account_owner_name || '',
            account_type: this.account_type || '',
            account_number: this.account_number || '',
            routing_number: this.routing_number || '',
            bank_name: this.bank_name || '',
            nickname: this.nickname || '',
            business_id: this.business_id || '',
        };
    }
}

const businessBankAccountSchema = (documents, allowOptionalFields) => {
    const schema = create$3({
        bank_name: bankNameValidation.required('Enter bank name'),
        nickname: nicknameValidation.required('Enter nickname'),
        account_owner_name: identityNameValidation.required('Enter account owner name'),
        account_type: accountTypeValidation.required('Select account type'),
        account_number: accountNumberValidation.required('Enter account number'),
        routing_number: routingNumberValidation.required('Enter routing number'),
        voided_check: voidedCheckValidation(documents, allowOptionalFields),
        bank_statement: bankStatementValidation(documents, allowOptionalFields)
    });
    const easySchema = create$3({
        bank_name: bankNameValidation.nullable(),
        nickname: nicknameValidation.nullable(),
        account_owner_name: identityNameValidation.nullable(),
        account_type: accountTypeValidation.nullable(),
        account_number: accountNumberValidation.nullable(),
        routing_number: routingNumberValidation.nullable(),
        voided_check: voidedCheckValidation(documents, allowOptionalFields),
        bank_statement: bankStatementValidation(documents, allowOptionalFields)
    });
    return allowOptionalFields ? easySchema : schema;
};

const BusinessBankAccountFormStepCore = /*@__PURE__*/ proxyCustomElement(class BusinessBankAccountFormStepCore extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.stepCompleteEvent = createEvent(this, "complete-form-step-event", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errors = {};
        this.bankAccount = {};
        this.existingDocuments = [];
        this.documentData = new EntityDocumentStorage();
        this.isLoading = false;
        this.initializeFormController = () => {
            this.formController = new FormController(businessBankAccountSchema(this.existingDocuments, this.allowOptionalFields));
            this.formController.setInitialValues(Object.assign({}, this.bankAccount));
            this.formController.errors.subscribe(errors => {
                this.errors = Object.assign({}, errors);
            });
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.storeFiles = (e) => {
            const fileList = Array.from(e.detail.fileList);
            const docType = e.detail.document_type;
            const documentList = fileList.map(file => new EntityDocument({ file, document_type: docType }, this.businessId));
            this.documentData[docType] = documentList;
        };
        this.getData = () => {
            this.isLoading = true;
            this.getBusiness({
                onSuccess: (response) => {
                    if (response.data.bank_accounts.length > 0) {
                        this.bankAccount = new BankAccount(response.data.bank_accounts[0]);
                    }
                    else {
                        this.bankAccount = new BankAccount({});
                        this.bankAccount.business_id = this.businessId;
                    }
                    this.existingDocuments = response.data.documents;
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => {
                    this.initializeFormController();
                    this.isLoading = false;
                }
            });
        };
        this.postDocumentRecordData = async (docData) => {
            this.isLoading = true;
            const payload = docData.record_data;
            return new Promise((resolve) => {
                this.postDocumentRecord({
                    payload,
                    onSuccess: (response) => {
                        resolve(this.handleDocRecordResponse(docData, response));
                    },
                    onError: ({ error, code, severity }) => {
                        this.errorEvent.emit({
                            message: error,
                            errorCode: code,
                            severity: severity
                        });
                        resolve(false);
                    },
                });
            });
        };
        this.handleDocRecordResponse = (docData, response) => {
            if (response.error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: response.error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: response.error,
                });
                return false;
            }
            else {
                docData.setPresignedUrl(response.data.presigned_url);
                return true;
            }
        };
        this.uploadDocument = async (docData) => {
            if (!docData.presigned_url) {
                throw new Error('Presigned URL is not set');
            }
            const response = await fetch(docData.presigned_url, {
                method: 'PUT',
                body: docData.fileString,
            });
            return this.handleUploadResponse(response);
        };
        this.handleUploadResponse = (response) => {
            if (response.error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: response.error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: response.error,
                });
                return false;
            }
            else {
                return true;
            }
        };
    }
    watchHandler(newValue) {
        this.formLoading.emit(newValue);
    }
    async validateAndSubmit({ onSuccess }) {
        if (this.existingBankAccount) {
            // If a bank account already exists, skip bank account posting; proceed directly to document upload
            this.sendData(onSuccess);
        }
        else {
            // If bank account is new, validate form inputs (bank account fields), then proceed
            this.formController.validateAndSubmit(() => this.sendData(onSuccess));
        }
    }
    componentWillLoad() {
        this.getBusiness && this.getData();
        this.formController = new FormController(businessBankAccountSchema(this.existingDocuments, this.allowOptionalFields));
    }
    componentDidLoad() {
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    get postPayload() {
        let formValues = new BankAccount(this.formController.values.getValue()).payload;
        return formValues;
    }
    get existingBankAccount() {
        var _a;
        return !!((_a = this.bankAccount) === null || _a === void 0 ? void 0 : _a.id);
    }
    async sendData(onSuccess) {
        try {
            //  Post bank account data if the form is not disabled
            const bankAccountPosted = !this.existingBankAccount ? await this.postBankAccountData() : true;
            if (!bankAccountPosted) {
                return;
            }
            // Post documents only after bank account was successfully created
            const documentsUploaded = await this.postBusinessDocuments();
            if (!documentsUploaded) {
                return;
            }
            onSuccess();
        }
        catch (error) {
            this.errorEvent.emit({
                message: error,
                errorCode: ComponentErrorCodes.POST_ERROR,
                severity: ComponentErrorSeverity.ERROR
            });
        }
    }
    async postBankAccountData() {
        let submittedData;
        this.isLoading = true;
        return new Promise((resolve) => {
            let success = false;
            this.postBankAccount({
                payload: this.postPayload,
                onSuccess: (response) => {
                    submittedData = response;
                    success = true;
                },
                onError: ({ error, code, severity }) => {
                    success = false;
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => {
                    this.stepCompleteEvent.emit({ response: submittedData, formStep: BusinessFormStep.bankAccount });
                    this.isLoading = false;
                    resolve(success);
                }
            });
        });
    }
    async postBusinessDocuments() {
        this.isLoading = true;
        try {
            const docArray = Object.values(this.documentData).flat();
            if (!docArray.length) {
                return true;
            }
            const recordsCreated = await Promise.all(docArray.map((docData) => this.postDocumentRecordData(docData))); // Create document records
            if (recordsCreated.includes(false)) {
                return false;
            } // Exit if document record creation fails
            const uploadsCompleted = await Promise.all(docArray.map((docData) => this.uploadDocument(docData))); // Upload documents to AWS presigned URLs
            if (uploadsCompleted.includes(false)) {
                return false;
            } // Exit if any upload fails
            return true;
        }
        finally {
            this.isLoading = false;
        }
    }
    render() {
        const bankAccountDefaultValue = this.formController.getInitialValues();
        if (this.isLoading) {
            return h(PaymentProvisioningLoading, null);
        }
        return (h("form", null, h("fieldset", { class: "mb-4" }, h("div", { class: "d-flex align-items-center gap-2" }, h("legend", { class: "mb-0", part: heading2 }, "Bank Account Info"), h("form-control-tooltip", { helpText: "This direct deposit account is the designated bank account where incoming funds will be deposited. The name of this account must match the registered business name exactly. We are not able to accept personal accounts unless your business is a registered sole proprietorship." })), h("hr", { class: "mt-2" }), h("bank-account-form-inputs", { defaultValue: bankAccountDefaultValue, errors: this.errors, inputHandler: this.inputHandler, formDisabled: this.existingBankAccount })), h("fieldset", { class: "mt-4" }, h("div", { class: "d-flex align-items-center gap-2" }, h("legend", { class: "mb-0", part: heading2 }, "Document Uploads"), h("form-control-tooltip", { helpText: "These documents are required for underwriting purposes. Various file formats such as PDF, DOC, DOCX, JPEG, and others are accepted. Multiple files can be uploaded for each document category." })), h("hr", { class: "mt-2" }), h("business-documents-on-file", { documents: this.existingDocuments, isLoading: this.isLoading }), h("bank-account-document-form-inputs", { inputHandler: this.inputHandler, errors: this.errors, storeFiles: this.storeFiles }))));
    }
    static get watchers() { return {
        "isLoading": ["watchHandler"]
    }; }
}, [0, "justifi-business-bank-account-form-step-core", {
        "businessId": [1, "business-id"],
        "getBusiness": [16, "get-business"],
        "postBankAccount": [16, "post-bank-account"],
        "postDocumentRecord": [16, "post-document-record"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "bankAccount": [32],
        "existingDocuments": [32],
        "documentData": [32],
        "isLoading": [32],
        "validateAndSubmit": [64]
    }, undefined, {
        "isLoading": ["watchHandler"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-bank-account-form-step-core", "bank-account-document-form-inputs", "bank-account-form-inputs", "business-documents-on-file", "custom-popper", "form-control-file", "form-control-select", "form-control-text", "form-control-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-bank-account-form-step-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessBankAccountFormStepCore);
            }
            break;
        case "bank-account-document-form-inputs":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "bank-account-form-inputs":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "business-documents-on-file":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-file":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessBankAccountFormStepCore as B, defineCustomElement as d };
